import { useState, useRef, DragEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useImport, EntityType } from '@/hooks/useImport';
import { ImportMapper } from '@/components/ImportMapper';
import { ImportPreview } from '@/components/ImportPreview';
import { ImportProgress } from '@/components/ImportProgress';
import { ImportResults } from '@/components/ImportResults';
import { cn } from '@/lib/utils';

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
}

const ENTITY_CONFIG = {
  leads: {
    label: 'Import Leads',
    description: 'Name, Company, Email, Phone, Source',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-600',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
      />
    ),
  },
  accounts: {
    label: 'Import Accounts',
    description: 'Name, Website, Phone, Industry, Type',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    ),
  },
  contacts: {
    label: 'Import Contacts',
    description: 'Name, Account, Email, Phone, Title',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-600',
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    ),
  },
} as const;

export function ImportDialog({ open, onClose }: ImportDialogProps) {
  const {
    state,
    setEntityType,
    handleFileSelect,
    setFieldMapping,
    proceedToPreview,
    canProceedFromMapping,
    getPreviewData,
    validateRows,
    startImport,
    goBack,
    reset,
  } = useImport();
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get validation results for preview
  const validation = validateRows();

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleImportAnother = () => {
    reset();
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDropZoneClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent
        className={cn(
          'transition-all duration-200',
          state.step === 'mapping' || state.step === 'preview' ? 'max-w-2xl' : 'max-w-md'
        )}
        onClose={state.step === 'importing' ? undefined : handleClose}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
            </div>
            <div>
              <DialogTitle>Import Data</DialogTitle>
              <DialogDescription>Import records from CSV files</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 pt-0">
          {state.step === 'select-entity' && (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Select the type of records you want to import.
              </p>

              <div className="space-y-3">
                {(Object.keys(ENTITY_CONFIG) as EntityType[]).map(entityType => {
                  const config = ENTITY_CONFIG[entityType];
                  return (
                    <button
                      key={entityType}
                      onClick={() => setEntityType(entityType)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 hover:bg-muted/50 transition-colors text-left"
                    >
                      <div
                        className={cn(
                          'w-8 h-8 rounded flex items-center justify-center shrink-0',
                          config.bgColor
                        )}
                      >
                        <svg
                          className={cn('w-4 h-4', config.textColor)}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          {config.icon}
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm text-foreground">{config.label}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {config.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {state.step === 'upload' && (
            <>
              <div className="flex items-center gap-2 mb-4">
                <button onClick={goBack} className="p-1 rounded hover:bg-muted transition-colors">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <p className="text-sm text-muted-foreground">
                  Upload a CSV file to import{' '}
                  <span className="font-medium text-foreground">{state.entityType}</span>
                </p>
              </div>

              {/* Error Message */}
              {state.error && (
                <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive">{state.error}</p>
                </div>
              )}

              {/* Loading State */}
              {state.isLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 animate-spin text-primary"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    <span className="text-sm text-muted-foreground">Parsing CSV...</span>
                  </div>
                </div>
              )}

              {/* Parsed Data Summary */}
              {state.parsedData && !state.isLoading && (
                <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-sm text-green-700">
                    ✓ Parsed {state.parsedData.length} rows with {state.headers?.length || 0}{' '}
                    columns
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Columns: {state.headers?.join(', ')}
                  </p>
                </div>
              )}

              {/* Drop Zone */}
              {!state.isLoading && !state.parsedData && (
                <div
                  onClick={handleDropZoneClick}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={cn(
                    'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                    isDragOver
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  <svg
                    className="w-8 h-8 mx-auto text-muted-foreground mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-sm font-medium text-foreground">Drop your CSV file here</p>
                  <p className="text-xs text-muted-foreground mt-1">or click to browse</p>
                </div>
              )}
            </>
          )}

          {/* Mapping Step */}
          {state.step === 'mapping' && state.entityType && state.headers && (
            <>
              <div className="flex items-center gap-2 mb-4">
                <button onClick={goBack} className="p-1 rounded hover:bg-muted transition-colors">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <p className="text-sm text-muted-foreground">
                  Map CSV columns to{' '}
                  <span className="font-medium text-foreground">{state.entityType}</span> fields
                </p>
              </div>

              {/* Error Message */}
              {state.error && (
                <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive">{state.error}</p>
                </div>
              )}

              {/* Import Mapper Component */}
              <div className="max-h-[400px] overflow-y-auto">
                <ImportMapper
                  headers={state.headers}
                  entityType={state.entityType}
                  mapping={state.fieldMapping}
                  onMappingChange={setFieldMapping}
                />
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                <Button variant="outline" onClick={goBack}>
                  Back
                </Button>
                <Button onClick={proceedToPreview} disabled={!canProceedFromMapping}>
                  Continue
                </Button>
              </div>
            </>
          )}

          {/* Preview Step */}
          {state.step === 'preview' && state.entityType && (
            <>
              <div className="flex items-center gap-2 mb-4">
                <button onClick={goBack} className="p-1 rounded hover:bg-muted transition-colors">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <p className="text-sm text-muted-foreground">
                  Review data before importing{' '}
                  <span className="font-medium text-foreground">{state.entityType}</span>
                </p>
              </div>

              {/* Error Message */}
              {state.error && (
                <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm text-destructive">{state.error}</p>
                </div>
              )}

              {/* Preview Component */}
              <div className="max-h-[400px] overflow-y-auto">
                <ImportPreview
                  previewData={getPreviewData(5)}
                  entityType={state.entityType}
                  totalRows={state.parsedData?.length || 0}
                  validCount={validation.valid}
                  invalidCount={validation.invalid}
                  mapping={state.fieldMapping}
                />
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                <Button variant="outline" onClick={goBack}>
                  Back
                </Button>
                <Button disabled={validation.valid === 0} onClick={startImport}>
                  Import {validation.valid} Records
                </Button>
              </div>
            </>
          )}

          {/* Importing Step */}
          {state.step === 'importing' && state.entityType && state.importProgress && (
            <ImportProgress progress={state.importProgress} entityType={state.entityType} />
          )}

          {/* Complete Step */}
          {state.step === 'complete' && state.entityType && state.importResult && (
            <ImportResults
              result={state.importResult}
              entityType={state.entityType}
              onClose={handleClose}
              onImportAnother={handleImportAnother}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
